<template>
  <v-menu
    v-model="menu"
    offset-y
    left
    max-width="420"
    min-width="360"
    :close-on-content-click="false"
    attach="#kt_header"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        v-on="on"
        @click="fetchNotifications"
      >
        <v-badge
          :content="unreadCount"
          :value="unreadCount > 0"
          color="red"
          overlap
          :class="{ 'notification-badge-active': unreadCount > 0 }"
        >
          <v-icon :class="{ 'notification-bell-ringing': unreadCount > 0 }">
            {{ unreadCount > 0 ? 'mdi-bell-ring-outline' : 'mdi-bell-outline' }}
          </v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="subtitle-1 d-flex align-center">
        <span>Notifications</span>

        <v-spacer />

        <v-btn
          text
          small
          :disabled="unreadCount === 0 || loading"
          @click="markAllAsRead"
        >
          Mark all as read
        </v-btn>
      </v-card-title>

      <v-divider class="my-0"/>

      <v-progress-linear
        v-if="loading"
        indeterminate
        height="2"
      />

      <v-list
        v-if="notifications.length"
        dense
        two-line
        class="py-0"
      >
        <v-list-item
          v-for="notification in notifications"
          :key="notification.id"
          :class="{ 'grey lighten-4': !notification.is_read }"
          @click="openNotification(notification)"
        >
          <v-list-item-avatar>
            <v-icon
              :color="notification.is_read ? 'grey' : 'success'"
            >
              {{ resolveIcon(notification) }}
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content class="notification-text">
            <v-list-item-title class="font-weight-medium">
              {{ notification.title }}
            </v-list-item-title>

            <v-list-item-subtitle class="notification-text">
              {{ notification.message }}
            </v-list-item-subtitle>

            <v-list-item-subtitle :class="['caption grey--text notification-text', !notification.is_read ? 'red--text' : 'green--text']">
              {{ formatDateTime(notification.created_at) }} - {{ !notification.is_read ? 'Unread' : 'Read' }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-icon
              small
              :color="!notification.is_read ? 'red' : 'green'"
            >
              mdi-circle
            </v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-card-text
        v-else-if="!loading"
        class="text-center grey--text"
      >
        No notifications yet.
      </v-card-text>

      <v-divider class="my-0"/>

      <v-card-actions>
        <v-btn
          block
          text
          color="primary"
          @click="goToNotificationsPage"
        >
          View all notifications
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import ApiService from '@/core/services/api.service'
import EventBus from '@/core/event-bus'

export default {
  name: 'HeaderNotifications',

  data() {
    return {
      menu: false,
      loading: false,
      notifications: [],
      unreadCount: 0,
      interval: null,
      lastNotificationId: null,
      initialized: false,
    }
  },

  created() {
    this.fetchNotifications()

    this.interval = setInterval(() => {
      this.fetchNotifications()
    }, 30000)
  },

  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },

  methods: {
    async fetchNotifications() {
      this.loading = true

      try {
        const response = await ApiService.get('notifications/recent')
        const data = response.data || {}
        const notifications = Array.isArray(data.notifications)
          ? data.notifications
          : []

        this.notifications = notifications
        this.unreadCount = data.unread_count || 0

        this.handleNewNotificationSnackbar(notifications)
      } catch (error) {
        console.error('Notifications fetch error', error)
      } finally {
        this.loading = false
      }
    },

    handleNewNotificationSnackbar(notifications) {
      if (!notifications.length) {
        return
      }
    
      const latestNotification = notifications[0]
    
      if (!this.initialized) {
        this.lastNotificationId = latestNotification.id
        this.initialized = true
        return
      }
    
      if (this.lastNotificationId === latestNotification.id) {
        return
      }
    
      this.lastNotificationId = latestNotification.id
    
      if (latestNotification.is_read) {
        return
      }
    
      EventBus.$emit('show-snackbar', {
        title: latestNotification.title || 'New notification',
        text: latestNotification.message || '',
        color: '#00aa00',
        timeout: 7000,
        url: latestNotification.data ? latestNotification.data.url : null,
      })
    },

    async openNotification(notification) {
        if (this.hasSelectedText()) {
            return
        }

        await this.markAsRead(notification)

        const data = notification.data || {}
        const url = data.url

        if (url) {
            this.menu = false
            this.$router.push(url)
        }
    },

    async markAsRead(notification) {
      if (notification.is_read) {
        return
      }

      try {
        await ApiService.post(`notifications/${notification.id}/read`, {})

        notification.is_read = true

        if (this.unreadCount > 0) {
          this.unreadCount -= 1
        }
      } catch (error) {
        console.error('Notification mark as read error', error)
      }
    },

    async markAllAsRead() {
      try {
        await ApiService.post('notifications/read-all', {})

        this.notifications = this.notifications.map((notification) => {
          return {
            ...notification,
            is_read: true,
          }
        })

        this.unreadCount = 0
      } catch (error) {
        console.error('Notifications mark all as read error', error)
      }
    },

    goToNotificationsPage() {
      this.menu = false

      this.$router.push('/notifications/notifications')
    },

    resolveIcon(notification) {
      if (notification.type === 'sales_order_document_paid') {
        return 'mdi-cash-check'
      }

      return 'mdi-bell-outline'
    },

    formatDateTime(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString()
    },

    hasSelectedText() {
        const selection = window.getSelection()

        if (!selection) {
            return false
        }

        return selection.toString().trim().length > 0
    },
  },
}
</script>
<style>
.v-badge__badge.red{
    color: #fff !important;
    background-color: #ff0000 !important;
}
.red--text{
    color: #ff0000 !important;
}
.green--text{
    color: #008300 !important;
}
.notification-text {
  user-select: text;
}

.notification-bell-ringing {
  transform-origin: top center;
  animation: notification-bell-ring 6.5s ease-in-out infinite;
}

.notification-badge-active {
  animation: notification-badge-pulse 6.5s ease-in-out infinite;
}

@keyframes notification-bell-ring {
  0% {
    transform: rotate(0deg);
  }

  3% {
    transform: rotate(14deg);
  }

  6% {
    transform: rotate(-12deg);
  }

  9% {
    transform: rotate(10deg);
  }

  12% {
    transform: rotate(-8deg);
  }

  15% {
    transform: rotate(6deg);
  }

  18% {
    transform: rotate(-4deg);
  }

  23% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(0deg);
  }
}

@keyframes notification-badge-pulse {
  0% {
    transform: scale(1);
  }

  10% {
    transform: scale(1.08);
  }

  20% {
    transform: scale(1);
  }

  100% {
    transform: scale(1);
  }
}
</style>